import * as THREE from "three";
import {EffectComposer} from "three/examples/jsm/postprocessing/EffectComposer";
import {OutlinePass} from "three/examples/jsm/postprocessing/OutlinePass";
import {RenderPass} from "three/examples/jsm/postprocessing/RenderPass";
import {ShaderPass} from "three/examples/jsm/postprocessing/ShaderPass";
import {FXAAShader} from "three/examples/jsm/shaders/FXAAShader";
import {GammaCorrectionShader} from "three/examples/jsm/shaders/GammaCorrectionShader.js";
import CameraAnimation from "../controls/CameraAnimation";
import {exp} from "three/nodes";

export default class THREECanvas {
    MAX_FPS = 60;

    constructor() {
        this.debug = true;
        this.clock = new THREE.Clock();
        this.scene = new THREE.Scene();
        this.camera = new THREE.PerspectiveCamera(75, window.innerWidth / window.innerHeight, 0.01, 1000);
        this.cameraAnimation = new CameraAnimation(this);
        this.sinceLastRender = 0;

        this._wall = null;
        // this._floor = null;
        this._outlinePasses = new Map();

        this.floorTexture = new THREE.TextureLoader().load("floor.svg");
        this.floorTexture.wrapS = THREE.RepeatWrapping;
        this.floorTexture.wrapT = THREE.RepeatWrapping;

        this._setup();
    }

    _setup() {
        this._setupRendering();
        this._setupBackground();
        this._setupLights();
        // this._setupFloor();
        window.addEventListener("resize", this._onWindowResize.bind(this));
        this.render();
    }

    setWall(wall) {
        if (this._wall === wall)
            return;
        if (this._wall !== null) {
            this.scene.remove(this._wall.wallMesh);
            this.scene.remove(this._wall.additionalMesh);
            this.scene.remove(this._wall.holdsMesh);
            // this.scene.remove(this._floor);
            // this._floor = null;
        }
        this._wall = wall;
        if (wall !== null) {
            this.scene.add(wall.wallMesh);
            this.scene.add(wall.additionalMesh);
            this.scene.add(wall.holdsMesh);

            const bbox = new THREE.Box3().setFromObject(wall.wallMesh);
            // let helper = new THREE.Box3Helper(bbox, new THREE.Color(0, 255, 0));
            // this.scene.add(helper);
            // this._floor = this._createFloor(bbox);
            // this.scene.add(this._floor);
        }
        this.render();
    }

    _createFloor(wallBbox) {
        const center = new THREE.Vector3();
        wallBbox.getCenter(center);

        const expandedBbox = wallBbox.clone().expandByScalar(5);
        const size = new THREE.Vector3();
        expandedBbox.getSize(size);

        const [width, height] = [size.x, size.z];
        const geometry = new THREE.PlaneGeometry(width, height, 8, 8);
        // const material = new THREE.MeshBasicMaterial({color: 0xf0f0f0, side: THREE.FrontSide});
        const material = new THREE.MeshBasicMaterial({map: this.floorTexture, side: THREE.FrontSide});
        this.floorTexture.repeat.set(width, height);
        // this.floorTexture.repeat.set(7, 7);
        const plane = new THREE.Mesh(geometry, material);
        plane.position.set(center.x, wallBbox.min.y - 0.01, center.z);
        plane.rotateX(-Math.PI / 2);
        return plane;
    }

    getCanvasElement() {
        return this.renderer.domElement;
    }

    _setupRendering(useFXAA = false) {
        const htmlCanvas = document.querySelector("#model-canvas")
        console.assert(htmlCanvas !== null);
        this.renderer = new THREE.WebGLRenderer({
            canvas: htmlCanvas,
            antialias: true,
        });

        this.renderer.setPixelRatio(window.devicePixelRatio);
        this.renderer.setSize(window.innerWidth, window.innerHeight);

        this.composer = new EffectComposer(this.renderer);

        this.renderPass = new RenderPass(this.scene, this.camera);
        this.composer.addPass(this.renderPass);

        const gammaCorrection = new ShaderPass(GammaCorrectionShader);
        this.composer.addPass(gammaCorrection);

        this.shaderPass = null;
        if (useFXAA) {
            this.shaderPass = new ShaderPass(FXAAShader);
            this.shaderPass.uniforms["resolution"].value.set(1 / window.innerWidth, 1 / window.innerHeight);
            this.shaderPass.renderToScreen = true;
            this.composer.addPass(this.shaderPass);
        }
    }

    _setupBackground() {
        // const backgroundTexture = new THREE.TextureLoader().load("sky.jpeg");
        // this.scene.background = backgroundTexture;


        const uniforms = {
            "topColor": {value: new THREE.Color(0xf0f5f0)},
            "bottomColor": {value: new THREE.Color(0x695442)},
            "offset": {value: 40},
            "exponent": {value: 0.8}
        };
        // uniforms[ "topColor" ].value.copy( hemiLight.color );

        // this.scene.fog.color.copy( uniforms[ "bottomColor" ].value );

        const skyGeo = new THREE.SphereGeometry(40, 16, 32);
        const skyMat = new THREE.ShaderMaterial({
            uniforms: uniforms,
            vertexShader: `
                varying vec3 vWorldPosition;

                void main() {
                    vec4 worldPosition = modelMatrix * vec4(position, 1.0);
                    vWorldPosition = worldPosition.xyz;
                    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
                }`,
            fragmentShader: `
                uniform vec3 topColor;
                uniform vec3 bottomColor;
                uniform float offset;
                uniform float exponent;

                varying vec3 vWorldPosition;

                void main() {
                    float h = normalize(vWorldPosition + offset).y;
                    gl_FragColor = vec4(mix(bottomColor, topColor, max(pow(max(h , 0.0), exponent), 0.0)), 1.0);
                }`,
            side: THREE.BackSide
        });
        // skyGeo.attributes.position.needsUpdate = true;
        // skyGeo.computeVertexNormals(true);

        const sky = new THREE.Mesh(skyGeo, skyMat);
        this.scene.add(sky);
    }

    _setupLights() {
        const ambientLight = new THREE.AmbientLight(0xffffff, 8);
        this.scene.add(ambientLight);

        // const hemisphereLight = new THREE.HemisphereLight(0xffffff, 0xffffff, 5);
        // hemisphereLight.position.set(0, 50, 0);
        // this.scene.add(hemisphereLight);

        // const directionalLight = new THREE.DirectionalLight(0xffffff, 1);
        // directionalLight.position.set(-1, 0.75, 1);
        // directionalLight.position.multiplyScalar(50);
        // this.scene.add(directionalLight);
    }


    _onWindowResize() {
        const width = window.innerWidth;
        const height = window.innerHeight;

        this.camera.aspect = width / height;
        this.camera.updateProjectionMatrix();

        // this.renderer.setPixelRatio(window.devicePixelRatio);
        // this.renderer.setSize(width, height);
        // this.composer.setSize(width, height);
        const htmlCanvas = document.querySelector("#model-canvas")
        htmlCanvas.style.width = width + "px";
        htmlCanvas.style.height = height + "px";

        if (this.shaderPass) {
            this.shaderPass.uniforms["resolution"].value.set(
                1 / window.innerWidth,
                1 / window.innerHeight
            );
        }

        this.render();
    }

    setOutlinedObjects(typeId, objects) {
        console.assert(this.isOutlineEnabled(typeId));
        this._outlinePasses.get(typeId).selectedObjects = objects;
    }

    enableOutline(typeId, params) {
        if (this.isOutlineEnabled(typeId))
            return;

        const outlinePass = new OutlinePass(
            new THREE.Vector2(window.innerWidth, window.innerHeight),
            this.scene,
            this.camera,
        );
        if (params.blending) {
            outlinePass.overlayMaterial.blending = params.blending;
        }
        outlinePass.edgeStrength = params.edgeStrength;
        outlinePass.edgeThickness = params.edgeThickness;
        outlinePass.visibleEdgeColor = params.color;
        outlinePass.hiddenEdgeColor = params.color;
        outlinePass.edgeGlow = params.edgeGlow;
        // Insert it right after the render pass.
        this.composer.insertPass(outlinePass, 1);
        this._outlinePasses.set(typeId, outlinePass);
    }

    disableOutline(typeId) {
        if (!this.isOutlineEnabled(typeId))
            return;
        this.composer.removePass(this._outlinePasses.get(typeId))
        this._outlinePasses.delete(typeId);
    }

    isOutlineEnabled(typeId) {
        return this._outlinePasses.has(typeId);
    }

    render(throttle = false) {
        this.sinceLastRender += this.clock.getDelta();
        const interval = 1 / this.MAX_FPS;
        if (this.sinceLastRender > interval || !throttle) {
            this.composer.render();
            // this.renderer.render(this.scene, this.camera);
            this.sinceLastRender %= interval;
        }
    }
}